import type { CaptionStyleConfig, RenderPreferences } from "@/remotion/schemas"

export interface StylePreset {
  id: string
  name: string
  description: string
  thumbnail?: string
  captionStyle: Partial<CaptionStyleConfig>
  renderPref?: Partial<RenderPreferences>
}

/**
 * Deep merge a partial style config into a full config.
 * Only overwrites fields that are explicitly set in the partial.
 */
export function applyPreset<T extends Record<string, any>>(
  base: T,
  partial: Partial<T>,
): T {
  const result = { ...base }
  for (const key of Object.keys(partial) as Array<keyof T>) {
    const value = partial[key]
    if (value !== undefined && value !== null && typeof value === "object" && !Array.isArray(value)) {
      result[key] = applyPreset(result[key] as any, value as any)
    } else if (value !== undefined) {
      result[key] = value as T[keyof T]
    }
  }
  return result
}

export const STYLE_PRESETS: StylePreset[] = [
  {
    id: "no_captions",
    name: "No Captions",
    description: "Disable captions entirely — ideal for foreign language or music videos",
    captionStyle: {},
    renderPref: { enableCaption: false },
  },
  {
    id: "beast",
    name: "Beast",
    description: "MrBeast-inspired bold karaoke with green highlights",
    thumbnail: "/presets/beast.png",
    captionStyle: {
      font: { family: "Montserrat Black", size: 58, weight: "900", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#04F827", secondary: "#FFFD03" },
      stroke: { enabled: true, color: "#000000", width: 10 },
      shadow: { enabled: true, color: "#000000", x: 3, y: 3, blur: 0 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "karaoke",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.15,
      position: "bottom",
      positionOffset: 10,
    },
  },
  {
    id: "creator",
    name: "Creator",
    description: "Clean professional style for educational content",
    thumbnail: "/presets/creator.png",
    captionStyle: {
      font: { family: "Inter Bold", size: 52, weight: "700", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FFD700", secondary: "#FFFFFF" },
      stroke: { enabled: true, color: "#000000", width: 6 },
      shadow: { enabled: true, color: "#000000", x: 0, y: 2, blur: 6 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "pop",
      textTransform: "none",
      activeWordEnabled: true,
      wordsPerCaption: 5,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "hustle",
    name: "Hustle",
    description: "High-energy scale style for business and motivation",
    thumbnail: "/presets/hustle.png",
    captionStyle: {
      font: { family: "Anton Regular", size: 60, weight: "400", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FF4444", secondary: "#FFFFFF" },
      stroke: { enabled: true, color: "#000000", width: 8 },
      shadow: { enabled: true, color: "#000000", x: 2, y: 2, blur: 0 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "scale",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.1,
      position: "bottom",
      positionOffset: 10,
    },
  },
  {
    id: "opus",
    name: "Opus",
    description: "OpusClip-style clean highlight with smooth emphasis",
    thumbnail: "/presets/opus.png",
    captionStyle: {
      font: { family: "Inter Bold", size: 54, weight: "700", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#00D4FF", secondary: "#FFFFFF" },
      stroke: { enabled: true, color: "#000000", width: 6 },
      shadow: { enabled: true, color: "#000000", x: 0, y: 2, blur: 8 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "karaoke",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "neon_glow",
    name: "Neon Glow",
    description: "Electric neon glow for energetic clips",
    thumbnail: "/presets/neon_glow.png",
    captionStyle: {
      font: { family: "Montserrat Black", size: 56, weight: "900", style: "normal" },
      colors: { default: "#E8F0FF", primary: "#15F4EE", secondary: "#FF00E5" },
      stroke: { enabled: true, color: "#080808", width: 6 },
      shadow: { enabled: true, color: "#6A00FF", x: 0, y: 0, blur: 12 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "pop",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "red_glow",
    name: "Red Glow",
    description: "Bold red glow for high-impact moments",
    thumbnail: "/presets/red_glow.png",
    captionStyle: {
      font: { family: "Rubik Black", size: 56, weight: "900", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FF3333", secondary: "#FFFFFF" },
      stroke: { enabled: true, color: "#000000", width: 7 },
      shadow: { enabled: true, color: "#FF0000", x: 0, y: 0, blur: 14 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "karaoke",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "highlight_box",
    name: "Highlight Box",
    description: "Background-highlighted active words for clarity",
    thumbnail: "/presets/highlight_box.png",
    captionStyle: {
      font: { family: "Inter ExtraBold", size: 54, weight: "800", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#121212", secondary: "#FFFD03" },
      stroke: { enabled: true, color: "#000000", width: 5 },
      shadow: { enabled: true, color: "#000000", x: 1, y: 1, blur: 2 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "karaoke-bg",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 5,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "underline",
    name: "Underline",
    description: "Underline emphasis on active words",
    thumbnail: "/presets/underline.png",
    captionStyle: {
      font: { family: "Barlow Condensed Bold", size: 58, weight: "700", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FFFD03", secondary: "#FFFFFF" },
      stroke: { enabled: true, color: "#000000", width: 7 },
      shadow: { enabled: true, color: "#000000", x: 2, y: 2, blur: 4 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "fade",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 5,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "karaoke",
    name: "Karaoke",
    description: "Classic karaoke word-by-word highlight",
    thumbnail: "/presets/karaoke.png",
    captionStyle: {
      font: { family: "Oswald Bold", size: 60, weight: "700", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FFD700", secondary: "#FF6B35" },
      stroke: { enabled: true, color: "#000000", width: 7 },
      shadow: { enabled: true, color: "#000000", x: 0, y: 2, blur: 4 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "karaoke",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 3,
      lineHeight: 1.15,
      position: "bottom",
      positionOffset: 10,
    },
  },
  {
    id: "retro",
    name: "Retro Neon",
    description: "Retro neon aesthetic for standout captions",
    thumbnail: "/presets/retro.png",
    captionStyle: {
      font: { family: "Bangers Regular", size: 58, weight: "400", style: "normal" },
      colors: { default: "#FFE4F0", primary: "#FF00FF", secondary: "#00FFFF" },
      stroke: { enabled: true, color: "#1A0026", width: 5 },
      shadow: { enabled: true, color: "#FF00FF", x: 0, y: 0, blur: 16 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "pop",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "magazine",
    name: "Magazine",
    description: "Editorial magazine-style typography",
    thumbnail: "/presets/magazine.png",
    captionStyle: {
      font: { family: "Barlow Condensed Black Italic", size: 60, weight: "900", style: "italic" },
      colors: { default: "#FFFFFF", primary: "#FF6B35", secondary: "#FFFFFF" },
      stroke: { enabled: true, color: "#000000", width: 6 },
      shadow: { enabled: true, color: "#000000", x: 3, y: 3, blur: 0 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "pop",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.1,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "cinematic",
    name: "Cinematic",
    description: "Subtle fade for storytelling and long-form",
    thumbnail: "/presets/cinematic.png",
    captionStyle: {
      font: { family: "Raleway Black", size: 52, weight: "900", style: "normal" },
      colors: { default: "#C6C6C6", primary: "#FFFFFF", secondary: "#E4E4E4" },
      stroke: { enabled: true, color: "#000000", width: 3 },
      shadow: { enabled: true, color: "#000000", x: 0, y: 2, blur: 18 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "fade",
      textTransform: "none",
      activeWordEnabled: false,
      wordsPerCaption: 5,
      lineHeight: 1.25,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "comic",
    name: "Comic",
    description: "Bold comic book style with pop animation",
    thumbnail: "/presets/comic.png",
    captionStyle: {
      font: { family: "Bangers Regular", size: 58, weight: "400", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FFFD03", secondary: "#FF6B6B" },
      stroke: { enabled: true, color: "#000000", width: 8 },
      shadow: { enabled: true, color: "#000000", x: 4, y: 4, blur: 0 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "pop",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 3,
      lineHeight: 1.15,
      position: "bottom",
      positionOffset: 10,
    },
  },
  {
    id: "gradient_pop",
    name: "Gradient Pop",
    description: "Vibrant gradient pop for viral content",
    thumbnail: "/presets/gradient_pop.png",
    captionStyle: {
      font: { family: "Montserrat Black", size: 56, weight: "900", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FF6FD8", secondary: "#3813C2" },
      stroke: { enabled: true, color: "#000000", width: 7 },
      shadow: { enabled: true, color: "#FF6FD8", x: 0, y: 0, blur: 10 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "pop",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "backdrop",
    name: "Backdrop",
    description: "Safe readability with background over busy footage",
    thumbnail: "/presets/backdrop.png",
    captionStyle: {
      font: { family: "Inter Bold", size: 50, weight: "700", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FFFD03", secondary: "#FFFFFF" },
      stroke: { enabled: false, color: "#000000", width: 0 },
      shadow: { enabled: false, color: "#000000", x: 0, y: 0, blur: 0 },
      backdrop: { enabled: true, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "fade",
      textTransform: "none",
      activeWordEnabled: true,
      wordsPerCaption: 5,
      lineHeight: 1.25,
      position: "bottom",
      positionOffset: 12,
    },
  },
  {
    id: "speech_bubble",
    name: "Speech Bubble",
    description: "Clean rounded backdrop with bold text",
    thumbnail: "/presets/speech_bubble.png",
    captionStyle: {
      font: { family: "Poppins Bold", size: 54, weight: "700", style: "normal" },
      colors: { default: "#111111", primary: "#FF4444", secondary: "#111111" },
      stroke: { enabled: false, color: "#000000", width: 0 },
      shadow: { enabled: true, color: "rgba(0,0,0,0.15)", x: 0, y: 4, blur: 16 },
      backdrop: { enabled: true, color: "rgba(255,255,255,0.95)", paddingX: 24, paddingY: 12, radius: 20 },
      animation: "pop",
      textTransform: "none",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 10,
    },
  },
  {
    id: "gaming",
    name: "Gaming",
    description: "Bold gaming style inspired by Fortnite captions",
    thumbnail: "/presets/gaming.png",
    captionStyle: {
      font: { family: "BurbankBigRegular-Black", size: 58, weight: "700", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FFD700", secondary: "#00BFFF" },
      stroke: { enabled: true, color: "#000000", width: 8 },
      shadow: { enabled: true, color: "#000000", x: 2, y: 2, blur: 0 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "scale",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 4,
      lineHeight: 1.15,
      position: "bottom",
      positionOffset: 10,
    },
  },
  {
    id: "flash",
    name: "Flash Words",
    description: "Quick flash emphasis on key words",
    thumbnail: "/presets/flash.png",
    captionStyle: {
      font: { family: "Russo One", size: 56, weight: "400", style: "normal" },
      colors: { default: "#FFFFFF", primary: "#FF3333", secondary: "#FFFD03" },
      stroke: { enabled: true, color: "#000000", width: 7 },
      shadow: { enabled: true, color: "#000000", x: 0, y: 0, blur: 6 },
      backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
      animation: "pop",
      textTransform: "uppercase",
      activeWordEnabled: true,
      wordsPerCaption: 3,
      lineHeight: 1.2,
      position: "bottom",
      positionOffset: 12,
    },
  },
]

/**
 * Get a preset by ID.
 */
export function getPresetById(id: string): StylePreset | undefined {
  return STYLE_PRESETS.find(p => p.id === id)
}
